import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import LoadingProgress from "../components/LoadingProgress";
import ErrorMessage from "../components/ErrorMessage";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { fetchSalesOperationTimes } from "../actions/sales";
import { fetchCustomers } from "../actions/customers";
import "../styles/DashboardView.css";

// ─── Data rules ──────────────────────────────────────────────────
// OperationTime is in seconds. 0 means "not measured" (historical sales,
// and PDA sales made before the PDA flow started reporting the timer).
// Same outlier cap the rest of the app uses (SalesView, SalesPlots):
// ignore times >= 500 s. Devoluciones (Amount < 0) copy the original
// sale's OperationTime, so they are excluded from time metrics.
const OUTLIER_CAP = 500;

// VACWAY TECH internal customer (stock / production machines). Its test
// sales are excluded from the KPIs unless explicitly selected in the filter.
const VACWAY_CUSTOMER_ID = "620d1664db950220fabc0a41";

const METHOD_ORDER = ["CASH", "TPV", "QR", "TOKEN", "PDA", "FREE", "CREDIT"];
const METHOD_LABELS = {
  TOKEN: "MANUAL",
  FREE: "GRATIS",
  CREDIT: "CRÉDITO",
};
const methodLabel = (m) => METHOD_LABELS[m] || m || "OTROS";

// Validated categorical palette (dataviz reference, light mode).
const SLOT1 = "#2a78d6"; // series 1 — blue
const SLOT2 = "#1baf7a"; // series 2 — aqua
const DEEMPH = "#c3c2b7"; // de-emphasis — "sin tiempo"
const GRID = "#e1e0d9"; // hairline grid
const AXIS_TICK = { fontSize: 10, fill: "#898781" };

// ─── Helpers ─────────────────────────────────────────────────────
function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function fmtSecs(s) {
  if (s === null || s === undefined || isNaN(s)) return "—";
  const total = Math.round(s);
  const m = Math.floor(total / 60);
  const sec = String(total % 60).padStart(2, "0");
  return `${m}:${sec} min`;
}

// Short form for axis ticks: "1:30"
function fmtSecsShort(s) {
  if (s === null || s === undefined || isNaN(s)) return "";
  const total = Math.round(s);
  const m = Math.floor(total / 60);
  const sec = String(total % 60).padStart(2, "0");
  return `${m}:${sec}`;
}

function average(arr) {
  if (!arr.length) return null;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

// Nearest-rank percentile on an already-sorted ascending array.
function percentileSorted(sorted, q) {
  if (!sorted.length) return null;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1));
  return sorted[idx];
}

function saleDay(sale) {
  const raw = sale.createdAt || sale.TimeStamp;
  return raw ? String(raw).slice(0, 10) : "";
}

function opTime(sale) {
  const t = Number(sale.OperationTime);
  return isNaN(t) ? 0 : t;
}

const isDevolucion = (sale) => Number(sale.Amount) < 0;
const hasValidTime = (sale) => {
  const t = opTime(sale);
  return !isDevolucion(sale) && t > 0 && t < OUTLIER_CAP;
};

// ─── Tooltip (values lead, labels follow; line keys in series color) ─
function TimeTooltip({ active, payload, label, valueFormatter }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.97)",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 12,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        pointerEvents: "none",
        zIndex: 1000,
      }}
    >
      {label && (
        <div style={{ fontWeight: 700, marginBottom: 6, color: "#111827" }}>
          {label}
        </div>
      )}
      {payload.map((entry, i) => {
        if (entry.value === null || entry.value === undefined) return null;
        const val = valueFormatter ? valueFormatter(entry.value, entry.dataKey) : entry.value;
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: i === 0 ? 0 : 3,
            }}
          >
            <span
              style={{
                width: 10,
                height: 3,
                borderRadius: 1,
                background: entry.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span style={{ fontWeight: 600, color: "#111827" }}>{val}</span>
            <span style={{ color: "#6b7280" }}>{entry.name}</span>
          </div>
        );
      })}
    </div>
  );
}

class TiemposView extends React.Component {
  constructor(props) {
    super(props);

    const now = new Date();
    const monthStr = localIso(now).slice(0, 7); // YYYY-MM

    this.state = {
      // committed period
      startDate: `${monthStr}-01`,
      endDate: localIso(now),
      periodTitle: "Mes actual",
      customer: "",
      // staged filter inputs
      tempPeriodType: "month",
      tempMonth: monthStr,
      tempStartDate: "",
      tempEndDate: "",
      tempCustomer: "",
      // data
      sales: null,
      loading: false,
      error: null,
    };
  }

  componentDidMount() {
    const { role, type } = this.props;
    if (role === "ADMIN" || (role === "CUSTOMER" && type === "GROUP")) {
      this.props.fetchCustomers();
    }
    this.load();
  }

  load = async () => {
    const { startDate, endDate, customer } = this.state;

    this.setState({ loading: true, error: null });
    try {
      const result = await this.props.fetchSalesOperationTimes(
        startDate,
        endDate,
        customer || undefined,
        undefined,
      );
      let rows = (result && result.response) || [];
      if (!customer && this.props.role === "ADMIN") {
        rows = rows.filter((s) => String(s.Customer) !== VACWAY_CUSTOMER_ID);
      }
      this.setState({ sales: rows });
    } catch (e) {
      this.setState({ error: e });
    } finally {
      this.setState({ loading: false });
    }
  };

  applyFilters = () => {
    const { tempPeriodType, tempMonth, tempStartDate, tempEndDate, tempCustomer } = this.state;

    let start = "";
    let end = "";
    let title = "";

    if (tempPeriodType === "month") {
      if (!tempMonth) return;
      const [y, m] = tempMonth.split("-");
      start = `${y}-${m}-01`;
      end = localIso(new Date(Number(y), Number(m), 0)); // last day of month
      title = `${m}/${y}`;
    } else {
      if (!tempStartDate || !tempEndDate) return;
      if (tempEndDate < tempStartDate) return;
      start = tempStartDate;
      end = tempEndDate;
      title = `${tempStartDate} → ${tempEndDate}`;
    }

    this.setState(
      { startDate: start, endDate: end, periodTitle: title, customer: tempCustomer },
      this.load,
    );
  };

  // ─── Aggregations ──────────────────────────────────────────────
  computeStats(rows) {
    const nonDevoluciones = rows.filter((s) => !isDevolucion(s));
    const devoluciones = rows.length - nonDevoluciones.length;
    const times = nonDevoluciones
      .filter(hasValidTime)
      .map(opTime)
      .sort((a, b) => a - b);

    return {
      total: nonDevoluciones.length,
      devoluciones,
      withTime: times.length,
      coverage: nonDevoluciones.length ? times.length / nonDevoluciones.length : 0,
      avg: average(times),
      median: percentileSorted(times, 0.5),
      p90: percentileSorted(times, 0.9),
      min: times.length ? times[0] : null,
      max: times.length ? times[times.length - 1] : null,
    };
  }

  computeHistogram(rows) {
    const buckets = [
      { label: "0–30 s", from: 0, to: 30 },
      { label: "30–60 s", from: 30, to: 60 },
      { label: "1–2 min", from: 60, to: 120 },
      { label: "2–3 min", from: 120, to: 180 },
      { label: "3–5 min", from: 180, to: 300 },
      { label: "5–8 min", from: 300, to: OUTLIER_CAP },
    ];
    const data = buckets.map((b) => ({ bucket: b.label, ventas: 0 }));
    rows.filter(hasValidTime).forEach((s) => {
      const t = opTime(s);
      const idx = buckets.findIndex((b) => t >= b.from && t < b.to);
      if (idx >= 0) data[idx].ventas += 1;
    });
    return data;
  }

  computeByMethod(rows) {
    const groups = {};
    rows.forEach((s) => {
      if (isDevolucion(s)) return;
      const m = s.PaymentMethod || "OTROS";
      if (!groups[m]) groups[m] = { total: 0, times: [] };
      groups[m].total += 1;
      if (hasValidTime(s)) groups[m].times.push(opTime(s));
    });

    const methods = [
      ...METHOD_ORDER.filter((m) => groups[m]),
      ...Object.keys(groups).filter((m) => !METHOD_ORDER.includes(m)).sort(),
    ];

    return methods.map((m) => {
      const times = groups[m].times.sort((a, b) => a - b);
      return {
        method: m,
        label: methodLabel(m),
        total: groups[m].total,
        withTime: times.length,
        withoutTime: groups[m].total - times.length,
        coverage: groups[m].total ? times.length / groups[m].total : 0,
        avg: average(times),
        median: percentileSorted(times, 0.5),
        p90: percentileSorted(times, 0.9),
        min: times.length ? times[0] : null,
        max: times.length ? times[times.length - 1] : null,
      };
    });
  }

  computeDaily(rows) {
    const byDay = {};
    rows.filter(hasValidTime).forEach((s) => {
      const day = saleDay(s);
      if (!day) return;
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(opTime(s));
    });

    return Object.keys(byDay)
      .sort()
      .map((day) => {
        const times = byDay[day].sort((a, b) => a - b);
        const [, m, d] = day.split("-");
        return {
          day: `${d}/${m}`,
          mediana: percentileSorted(times, 0.5),
          p90: percentileSorted(times, 0.9),
          ventas: times.length,
        };
      });
  }

  // ─── Render pieces ─────────────────────────────────────────────
  renderKpi(cls, icon, label, value, sub) {
    return (
      <div
        className={`dashboard-kpi-card ${cls}`}
        key={label}
        style={{
          minHeight: 96,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "10px 12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <i className={`fas fa-${icon}`} style={{ fontSize: 12, color: "#6b7280" }}></i>
          <div className="kpi-label" style={{ margin: 0, fontSize: 12 }}>
            {label}
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div className="kpi-value" style={{ fontSize: 20, fontWeight: 700 }}>
            {value}
          </div>
        </div>
        {sub && (
          <div className="small text-muted" style={{ marginTop: 6 }}>
            {sub}
          </div>
        )}
      </div>
    );
  }

  renderEmpty() {
    return (
      <div className="text-muted small" style={{ textAlign: "center", padding: "40px 0" }}>
        Sin datos para el periodo seleccionado
      </div>
    );
  }

  render() {
    const { role, type, customers } = this.props;
    const {
      sales, loading, error, periodTitle,
      tempPeriodType, tempMonth, tempStartDate, tempEndDate, tempCustomer,
    } = this.state;

    if (error) return <ErrorMessage message={error.message || "Error cargando datos"} />;
    if (!sales && loading) return <LoadingProgress />;
    if (!sales) return <LoadingProgress />;

    const stats = this.computeStats(sales);
    const histogram = this.computeHistogram(sales);
    const byMethod = this.computeByMethod(sales);
    const daily = this.computeDaily(sales);
    const hasTimes = stats.withTime > 0;

    const canPickCustomer = role === "ADMIN" || (role === "CUSTOMER" && type === "GROUP");
    const customerOptions = (customers || []).filter(
      (c) => c.Type === "GENERAL" || c.Type === "LICENCIA_BASICA" || c.Type === "LICENCIA_PRO",
    );

    // Refetch keeps the frame: previous charts at reduced opacity, no flash.
    const refetchStyle = loading ? { opacity: 0.55, pointerEvents: "none" } : undefined;

    return (
      <div className="dashboard-view">
        {/* ─── Header + filter row ─── */}
        <div
          className="dashboard-header"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              <i className="fas fa-stopwatch"></i>
              <span className="text-vw-dark"> Tiempos</span>
              <span className="text-vw-light"> de operación</span>
            </h1>
            <div className="small text-muted mt-1">Período: {periodTitle}</div>
          </div>

          <div className="dashboard-actions">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
              <select
                className="dashboard-filter-input"
                value={tempPeriodType}
                onChange={(e) => this.setState({ tempPeriodType: e.target.value })}
                style={{ width: 110 }}
              >
                <option value="month">Mes</option>
                <option value="range">Rango</option>
              </select>

              {tempPeriodType === "month" ? (
                <input
                  type="month"
                  className="dashboard-filter-input"
                  value={tempMonth}
                  onChange={(e) => this.setState({ tempMonth: e.target.value })}
                />
              ) : (
                <>
                  <input
                    type="date"
                    className="dashboard-filter-input"
                    value={tempStartDate}
                    onChange={(e) => this.setState({ tempStartDate: e.target.value })}
                  />
                  <input
                    type="date"
                    className="dashboard-filter-input"
                    value={tempEndDate}
                    onChange={(e) => this.setState({ tempEndDate: e.target.value })}
                  />
                </>
              )}

              {canPickCustomer && (
                <select
                  className="dashboard-filter-input"
                  value={tempCustomer}
                  onChange={(e) => this.setState({ tempCustomer: e.target.value })}
                  style={{ maxWidth: 220 }}
                >
                  <option value="">Todos los clientes</option>
                  {customerOptions.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.Fullname}
                    </option>
                  ))}
                </select>
              )}

              <button
                type="button"
                className="dashboard-filter-input toggle-filters"
                onClick={this.applyFilters}
                disabled={loading}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
              >
                <i className="fas fa-sync-alt"></i>
                Cargar
              </button>
            </div>
          </div>
        </div>

        <div style={refetchStyle}>
          {/* ─── KPI row ─── */}
          <div className="dashboard-kpi-wrapper">
            <div className="dashboard-kpi-row">
              {this.renderKpi("total", "shopping-cart", "Ventas", stats.total.toLocaleString())}
              {this.renderKpi(
                "occupancy",
                "percentage",
                "Con tiempo registrado",
                stats.withTime.toLocaleString(),
                `${(stats.coverage * 100).toFixed(1)}% del total`,
              )}
              {this.renderKpi("time", "stopwatch", "Tiempo medio", fmtSecs(stats.avg))}
              {this.renderKpi("time", "clock", "Mediana", fmtSecs(stats.median))}
              {this.renderKpi(
                "time",
                "hourglass-half",
                "P90",
                fmtSecs(stats.p90),
                "El 90% termina antes de este tiempo",
              )}
            </div>
          </div>

          <div className="small text-muted" style={{ margin: "4px 2px 12px" }}>
            Las métricas de tiempo excluyen ventas sin tiempo registrado (0 s), atípicos ≥{" "}
            {fmtSecs(OUTLIER_CAP)} y devoluciones
            {stats.devoluciones > 0 ? ` (${stats.devoluciones} en este periodo)` : ""}. El tiempo
            mide desde la primera interacción del usuario hasta el ticket.
          </div>

          {/* ─── Charts ─── */}
          <div
            className="dashboard-graphs-grid"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))" }}
          >
            {/* Distribución de tiempos */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i className="fas fa-chart-bar" style={{ marginRight: 8 }}></i>
                Distribución de tiempos
              </div>
              <div className="card-body-custom">
                {!hasTimes ? (
                  this.renderEmpty()
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={histogram} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke={GRID} vertical={false} />
                      <XAxis dataKey="bucket" tick={AXIS_TICK} interval={0} />
                      <YAxis tick={AXIS_TICK} allowDecimals={false} width={40} />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        content={<TimeTooltip valueFormatter={(v) => v.toLocaleString()} />}
                      />
                      <Bar
                        dataKey="ventas"
                        name="Ventas"
                        fill={SLOT1}
                        barSize={24}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Tiempo por método de pago */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i className="fas fa-credit-card" style={{ marginRight: 8 }}></i>
                Tiempo por método de pago
              </div>
              <div className="card-body-custom">
                {!hasTimes ? (
                  this.renderEmpty()
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={byMethod.filter((m) => m.withTime > 0)}
                      margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
                    >
                      <CartesianGrid stroke={GRID} vertical={false} />
                      <XAxis dataKey="label" tick={AXIS_TICK} interval={0} />
                      <YAxis tick={AXIS_TICK} tickFormatter={fmtSecsShort} width={45} />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        content={<TimeTooltip valueFormatter={(v) => fmtSecs(v)} />}
                      />
                      <Bar dataKey="median" name="Mediana" fill={SLOT1} barSize={14} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="p90" name="P90" fill={SLOT2} barSize={14} radius={[4, 4, 0, 0]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Evolución diaria */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i className="fas fa-chart-line" style={{ marginRight: 8 }}></i>
                Evolución diaria
              </div>
              <div className="card-body-custom">
                {daily.length === 0 ? (
                  this.renderEmpty()
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={daily} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke={GRID} vertical={false} />
                      <XAxis dataKey="day" tick={AXIS_TICK} interval="preserveStartEnd" />
                      <YAxis tick={AXIS_TICK} tickFormatter={fmtSecsShort} width={45} />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        content={<TimeTooltip valueFormatter={(v) => fmtSecs(v)} />}
                      />
                      <Line
                        type="monotone"
                        dataKey="mediana"
                        name="Mediana"
                        stroke={SLOT1}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="p90"
                        name="P90"
                        stroke={SLOT2}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Cobertura por método */}
            <div className="dashboard-card-section">
              <div className="card-header-custom">
                <i className="fas fa-database" style={{ marginRight: 8 }}></i>
                Cobertura de medición por método
              </div>
              <div className="card-body-custom">
                {byMethod.length === 0 ? (
                  this.renderEmpty()
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={byMethod} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid stroke={GRID} vertical={false} />
                      <XAxis dataKey="label" tick={AXIS_TICK} interval={0} />
                      <YAxis tick={AXIS_TICK} allowDecimals={false} width={40} />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        content={<TimeTooltip valueFormatter={(v) => v.toLocaleString()} />}
                      />
                      <Bar
                        dataKey="withTime"
                        name="Con tiempo"
                        stackId="cov"
                        fill={SLOT1}
                        barSize={24}
                        stroke="#ffffff"
                        strokeWidth={2}
                      />
                      <Bar
                        dataKey="withoutTime"
                        name="Sin tiempo"
                        stackId="cov"
                        fill={DEEMPH}
                        barSize={24}
                        radius={[4, 4, 0, 0]}
                        stroke="#ffffff"
                        strokeWidth={2}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* ─── Table view (accessible twin of the charts) ─── */}
          <div className="dashboard-card-section" style={{ marginTop: 16 }}>
            <div className="card-header-custom">
              <i className="fas fa-table" style={{ marginRight: 8 }}></i>
              Detalle por método de pago
            </div>
            <div className="card-body-custom" style={{ overflowX: "auto" }}>
              <table className="table table-sm" style={{ fontSize: 13, marginBottom: 0 }}>
                <thead>
                  <tr className="text-muted">
                    <th>Método</th>
                    <th className="text-right">Ventas</th>
                    <th className="text-right">Con tiempo</th>
                    <th className="text-right">Cobertura</th>
                    <th className="text-right">Media</th>
                    <th className="text-right">Mediana</th>
                    <th className="text-right">P90</th>
                    <th className="text-right">Mín</th>
                    <th className="text-right">Máx</th>
                  </tr>
                </thead>
                <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                  {byMethod.map((m) => (
                    <tr key={m.method}>
                      <td>{m.label}</td>
                      <td className="text-right">{m.total.toLocaleString()}</td>
                      <td className="text-right">{m.withTime.toLocaleString()}</td>
                      <td className="text-right">{(m.coverage * 100).toFixed(1)}%</td>
                      <td className="text-right">{fmtSecs(m.avg)}</td>
                      <td className="text-right">{fmtSecs(m.median)}</td>
                      <td className="text-right">{fmtSecs(m.p90)}</td>
                      <td className="text-right">{fmtSecs(m.min)}</td>
                      <td className="text-right">{fmtSecs(m.max)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700, borderTop: "2px solid #e1e0d9" }}>
                    <td>TOTAL</td>
                    <td className="text-right">{stats.total.toLocaleString()}</td>
                    <td className="text-right">{stats.withTime.toLocaleString()}</td>
                    <td className="text-right">{(stats.coverage * 100).toFixed(1)}%</td>
                    <td className="text-right">{fmtSecs(stats.avg)}</td>
                    <td className="text-right">{fmtSecs(stats.median)}</td>
                    <td className="text-right">{fmtSecs(stats.p90)}</td>
                    <td className="text-right">{fmtSecs(stats.min)}</td>
                    <td className="text-right">{fmtSecs(stats.max)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  role: state.auth.role,
  type: state.auth.type,
  customers: state.customers.customers,
});

const mapDispatchToProps = (dispatch) => ({
  fetchSalesOperationTimes: bindActionCreators(fetchSalesOperationTimes, dispatch),
  fetchCustomers: bindActionCreators(fetchCustomers, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(TiemposView);
